import { useTheme } from '@/context/ThemeContext';

const industries = [
  {
    icon: 'ri-tools-line',
    name: 'HVAC & Home Services',
    desc: 'Capture every inbound call while your team is in the field. Book more jobs without adding staff.',
    stat: '+40%',
    statLabel: 'More Booked Jobs',
    img: 'https://public.readdy.ai/ai/img_res/8f063b2e4d8d9062bd528c17c119f7c1.jpg',
  },
  {
    icon: 'ri-scales-line',
    name: 'Law Firms',
    desc: 'Automate client intake and ensure no consultation opportunity is missed.',
    stat: '+30%',
    statLabel: 'More Consultations',
    img: 'https://readdy.ai/api/search-image?query=modern%20law%20office%20interior%2C%20elegant%20dark%20wood%20desk%2C%20legal%20books%2C%20professional%20atmosphere%2C%20dramatic%20window%20lighting%2C%20cinematic%20photography%2C%20dark%20moody%20tones%2C%20high%20contrast%2C%20no%20people&width=800&height=500&seq=ind-law1&orientation=landscape',
  },
  {
    icon: 'ri-building-line',
    name: 'Real Estate & Property Management',
    desc: 'Respond instantly to inquiries, leasing requests, and tenant communication.',
    stat: '2s',
    statLabel: 'Avg Response Time',
    img: 'https://readdy.ai/api/search-image?query=modern%20luxury%20apartment%20building%20exterior%20at%20dusk%2C%20dramatic%20sky%2C%20architectural%20photography%2C%20clean%20lines%2C%20glass%20facade%2C%20urban%20setting%2C%20cinematic%20lighting%2C%20high%20contrast%2C%20professional%20real%20estate%20photography&width=800&height=500&seq=ind-re1&orientation=landscape',
  },
  {
    icon: 'ri-heart-pulse-line',
    name: 'Healthcare & Wellness',
    desc: 'Automate appointment booking and patient engagement without front desk overload.',
    stat: '24/7',
    statLabel: 'Patient Coverage',
    img: 'https://readdy.ai/api/search-image?query=modern%20medical%20clinic%20reception%20area%2C%20clean%20white%20interior%2C%20minimal%20design%2C%20professional%20healthcare%20environment%2C%20soft%20lighting%2C%20contemporary%20furniture%2C%20no%20people%2C%20architectural%20photography&width=800&height=500&seq=ind-health1&orientation=landscape',
  },
];

export default function IndustriesSection() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section
      id="industries"
      className={`relative py-28 md:py-36 overflow-hidden transition-colors duration-500 ${isDark ? 'bg-[#050505]' : 'bg-white'}`}
    >
      <div className={`absolute top-0 left-0 w-full h-px ${isDark ? 'bg-gradient-to-r from-transparent via-[#38D6D6]/20 to-transparent' : 'bg-gradient-to-r from-transparent via-[#38D6D6]/30 to-transparent'}`} />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-px bg-[#38D6D6]/60"></div>
          <span className="text-xs font-grotesk text-[#38D6D6] tracking-widest uppercase">Industries</span>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <h2 className={`font-sora font-800 text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}>
            Built for<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#38D6D6] to-[#4FA3FF]">
              Revenue-Critical
            </span>{' '}
            Industries
          </h2>
          <p className={`font-grotesk text-base max-w-sm leading-relaxed ${isDark ? 'text-white/45' : 'text-black/45'}`}>
            Every industry has unique lead flow challenges. Our systems are configured for your specific operational reality.
          </p>
        </div>

        {/* Industry grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {industries.map((ind, i) => (
            <div
              key={i}
              className={`group relative rounded-2xl border overflow-hidden transition-all duration-500 hover:-translate-y-1 cursor-default ${
                isDark ? 'border-white/8' : 'border-black/8'
              }`}
            >
              {/* Image */}
              <div className="relative w-full h-52 overflow-hidden">
                <img
                  src={ind.img}
                  alt={ind.name}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-b from-black/20 to-black/80' : 'bg-gradient-to-b from-black/10 to-black/70'}`}></div>
                {/* Stat overlay */}
                <div className="absolute top-5 right-5">
                  <div className="text-right">
                    <div className="font-sora font-800 text-3xl text-transparent bg-clip-text bg-gradient-to-r from-[#38D6D6] to-[#4FA3FF]">
                      {ind.stat}
                    </div>
                    <div className="font-grotesk text-xs text-white/60 mt-0.5">{ind.statLabel}</div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className={`p-7 ${isDark ? 'bg-[#0D1117]' : 'bg-white'}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#38D6D6]/10 border border-[#38D6D6]/20">
                    <i className={`${ind.icon} text-[#38D6D6] text-base`}></i>
                  </div>
                  <h3 className={`font-sora font-600 text-lg ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}>{ind.name}</h3>
                </div>
                <p className={`font-grotesk text-sm leading-relaxed mb-5 ${isDark ? 'text-white/50' : 'text-black/50'}`}>{ind.desc}</p>
                <div className="flex items-center gap-1.5 text-[#38D6D6] text-sm font-grotesk cursor-pointer group-hover:gap-2.5 transition-all">
                  <span>View Solutions</span>
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={`absolute bottom-0 left-0 w-full h-px ${isDark ? 'bg-gradient-to-r from-transparent via-white/5 to-transparent' : 'bg-gradient-to-r from-transparent via-black/5 to-transparent'}`} />
    </section>
  );
}
